import { useState } from "react"
import { AlertBox, Button, ButtonGroup, FormContainer, FormDescription, FormGroup, FormTitle, Input, Label, LanguageToggle, StepItem, StepLabel, StepNumber, StepperContainer, StepperWrapper, Textarea } from "./styles"
import { useTranslation } from "react-i18next"
import { postData } from "../../../backend/api"
import LanguageTooltip from "../tooltip"
import CustomSelect from "../select"

const STEPS = [
    { number: 1, label: "Location" },
    { number: 2, label: "Category" },
    { number: 3, label: "Description" },
    { number: 4, label: "Check" },
    { number: 5, label: "Password" },
]

export default function StepperForm({ formFields, setLoaderBox, setMessage, api }) {
  const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState(1)
    const [formData, setFormData] = useState({})

    const handleNext = (data,nextStep=true) => {
      if(data)
        setFormData((prev) => ({ ...prev, ...data }))
      if(nextStep)
        setCurrentStep((prev) => Math.min(prev + 1, STEPS.length))
    }

    const handleBack = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 1))
    }

    const getStepContent = (step) => {
        switch (step) {
            case 1:
                formFields.filter((field) => field.name === "location").forEach((field) => field.disabled = false);
                return {
                    title: t("relevantLocationTitle"),
                    description: t("relevantLocationDescription"),
                    fields: formFields.filter((field) => field.name === "location"),
                }
            case 2:
                formFields.filter((field) => field.name === "category").forEach((field) => field.disabled = false);
                return {
                    title: t("incidentCategoryTitle"),
                    description: t("incidentCategoryDescription"),
                    fields: formFields.filter((field) => field.name === "category"),
                }
            case 3:
                formFields.filter((field) => ["title", "description"].includes(field.name)).forEach((field) => field.disabled = false);
                return {
                    title: t("incidentInfoTitle"),
                    description: t("incidentInfoDescription"),
                    fields: formFields.filter((field) => ["title", "description"].includes(field.name)),
                }
            case 4:
                formFields.filter((field) => field.name !== "pass").forEach((field) => field.disabled = true);
                return {
                    title: t("reviewTitle"),
                    description: t("reviewDescription"),
                    fields: formFields.filter((field) => field.name !== "pass"), // Include all fields for review
                }
            case 5:
                return {
                    title: t("savePassTitle"),
                    description: t("savePassDescription"),
                    fields: formFields.filter((field) => field.name === "pass"),
                    bottomTitle: t("savePassBottomTitle"),
                    bottomDescription: t("savePassBottomDescription"),
                }
            default:
                return { title: "", description: "", fields: [] }
        }
    }

    const stepContent = getStepContent(currentStep)

    const handleFinishedForm = ()=>{
      setFormData({});
      setCurrentStep(1);
    }

    return (
        <StepperContainer>
          <div style={{width:'100%',display:'flex',justifyContent:'end',alignItems:'center'}}>
            <LanguageTooltip page={"login"} />
            </div>
            <StepperWrapper>
                {STEPS.map((step, index) => (
                    <StepItem key={step.number} active={currentStep === step.number} completed={currentStep > step.number}>
                        <StepNumber active={currentStep === step.number} completed={currentStep > step.number}>
                            {step.number}
                        </StepNumber>
                        <StepLabel active={currentStep === step.number}>{t(step.label)}</StepLabel>
                    </StepItem>
                ))}
            </StepperWrapper>

            <FormStep
                title={stepContent.title}
                description={stepContent.description}
                bottomTitle={stepContent?.bottomTitle}
                bottomDescription={stepContent?.bottomDescription}
                data={formData}
                onNext={handleNext}
                onBack={(currentStep > 1 && currentStep < 5) ? handleBack : undefined}
                formFields={stepContent.fields}
                step={currentStep}
                setLoaderBox={setLoaderBox}
                setMessage={setMessage}
                api={api}
                handleFinishedForm={handleFinishedForm}
            />
        </StepperContainer>
    )
}

const FormStep = ({ data, onNext, onBack, formFields = [], title, description,bottomTitle,bottomDescription, step,setLoaderBox,setMessage, api, handleFinishedForm }) => {
    const { t } = useTranslation();

    const [copied, setCopied] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault()
      const formData = new FormData(e.target)
      const values = {}
      formFields.forEach((field) => {
        if(formData.get(field.name))
          values[field.name] = formData.get(field.name)
        else
          return;
      })
      if (step === 4) {
        try {
          setLoaderBox(true);
          await postData(data, api)
        .then((response) => {
          if (response.status === 200) {
            setMessage({ type: 1, content: t("dataSubmit"), proceed: "Thank you!" });
            values.pass = response.data;
            onNext(values);
            // setFormInput(initialFormInout);
          } else {
            setMessage({ type: 1, content: "Somethig went wrong", proceed: "Try Again" });
          }
          setLoaderBox(false);
        })
        .catch(() => {
          setMessage({ type: 1, content: "An error occurred on the server. Please try again later or contact support if the issue persists.", proceed: "Try Again" });
          setLoaderBox(false);
        });
        } catch (error) {
          console.error('Error submitting form:', error)
        }
      } else {
        onNext(values)
      }
    }

    const handleCopy = (value) => {
      navigator.clipboard.writeText(value).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    };

    const handleSelectChange = (index, event, id, type = "text", sub = null, data = {}) => {
        onNext({[id]: event.id}, false)
    }

    return (
      <FormContainer>
        <FormTitle>{t(title)}</FormTitle>
        <FormDescription>{t(description)}</FormDescription>
        {step > 0 && step < 5 && <AlertBox>{t("anonymousMessage")}</AlertBox>}
        <form onSubmit={handleSubmit}>
          {formFields.map((field) => {
            switch (field.type) {
              // Render a regular text input
              case "text":
              case "password":
              case "email":
              case "color": return (<FormGroup key={field.name} style={{ position: 'relative' }}>
              <Label htmlFor={field.name}>{t(field.label)}</Label>
              <Input
                type={field.type}
                id={field.name}
                name={field.name}
                placeholder={t(field?.placeholder)}
                defaultValue={data[field.name] || field.default}
                minLength={field?.minimum}
                maxLength={field?.maximum}
                required={field?.required}
                disabled={field?.disabled}
                className={`${field.name}`}
              />
              {field.copy && (
                <Button
                  variant="primary"
                  style={{ position: 'absolute', right: window.innerWidth > 768 ? 0 : -20, top: '69.6%', transform: 'translateY(-50%)' }}
                  type="button"
                  onClick={() => handleCopy(data[field.name] || field.default)}
                >
                  {copied ? t("copied") : t(field.copy)}
                </Button>
              )}
            </FormGroup>);

            case "textarea": return (
              <FormGroup key={field.name} style={{ position: 'relative' }}>
              <Label htmlFor={field.name}>{t(field.label)}</Label>
              <Textarea
                type={field.type}
                id={field.name}
                name={field.name}
                placeholder={t(field?.placeholder)}
                defaultValue={data[field.name] || field.default}
                minLength={field?.minimum}
                maxLength={field?.maximum}
                required={field?.required}
                disabled={field?.disabled}
                className={`${field.name}`}
              />
            </FormGroup>
            );

            case "select": return (
              <FormGroup onClick={(e) => e.preventDefault()} key={field.name} style={{ position: 'relative' }}>
              {/* <Label htmlFor={field.name}>{t(field.label)}</Label> */}
              <CustomSelect {...field} name={field.name} id={field.name} value={data[field.name] || field.default} onSelect={handleSelectChange}></CustomSelect>
            </FormGroup>
            )
            }})}
          {bottomTitle && <FormTitle>{t(bottomTitle)}</FormTitle>}
          {bottomDescription && <FormDescription>{t(bottomDescription)}</FormDescription>}
            <ButtonGroup>
              {onBack && (
                <Button type="button" onClick={onBack}>
                  {t("back")}
                </Button>
              )}
              {step===5 ? <Button onClick={handleFinishedForm} style={{letterSpacing:'0.8px'}} variant="primary">
                {t("finish")}
              </Button> : <Button type="submit" variant="primary">
                {step === 4 ? t("submit") : t("next")}
              </Button>}
            </ButtonGroup>
        </form>
      </FormContainer>
    )
}